export const MINIMON_LOADER_STARTED = 0x01;
export const MINIMON_APPLICATION_STARTED = 0x03;
export class BootmodeHandshakeError extends Error {
    stage;
    constructor(message, stage) {
        super(`Bootmode handshake failed at stage "${stage}": ${message}`);
        this.stage = stage;
        this.name = 'BootmodeHandshakeError';
    }
}
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export async function performHandshake(transport, opts) {
    if (opts.primaryLoader.length !== 32) {
        throw new BootmodeHandshakeError(`primary loader must be exactly 32 bytes (got ${opts.primaryLoader.length})`, 'init');
    }
    const primaryAck = opts.primaryAck ?? MINIMON_LOADER_STARTED;
    const secondaryAck = opts.secondaryAck ?? MINIMON_APPLICATION_STARTED;
    const byteTimeoutMs = opts.byteTimeoutMs ?? 200;
    const interByteDelayMs = opts.interByteDelayMs ?? 0;
    // Step 1-2: send auto-baud calibration byte. Transport consumes the
    // K-line echo of the 0x00 itself.
    transport.flushInput();
    await transport.write(new Uint8Array([0x00]));
    // Step 3: BSL identification byte.
    let idByte;
    try {
        const resp = await transport.read(1, byteTimeoutMs * 5);
        idByte = resp[0];
    }
    catch (err) {
        throw new BootmodeHandshakeError(`no response to BSL probe (BOOT pin not grounded? wrong K-line wiring? wrong baud rate?): ${err.message}`, 'probe');
    }
    if (idByte !== opts.expectedIdByte) {
        throw new BootmodeHandshakeError(`wrong BSL identification byte: expected 0x${opts.expectedIdByte
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}, got 0x${idByte.toString(16).padStart(2, '0').toUpperCase()}`, 'probe');
    }
    // Step 4: upload primary loader byte-by-byte with echo verify.
    await uploadEchoed(transport, opts.primaryLoader, 'primary', interByteDelayMs, byteTimeoutMs, opts.onProgress);
    // Step 5: primary's "I_LOADER_STARTED" acknowledgement.
    const primaryAckByte = (await transport.read(1, byteTimeoutMs * 10))[0];
    if (primaryAckByte !== primaryAck) {
        throw new BootmodeHandshakeError(`primary loader did not start: expected 0x${primaryAck
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}, got 0x${primaryAckByte.toString(16).padStart(2, '0').toUpperCase()}`, 'primary-ack');
    }
    // Step 6: upload secondary loader byte-by-byte with echo verify by the primary.
    await uploadEchoed(transport, opts.secondaryLoader, 'secondary', interByteDelayMs, byteTimeoutMs, opts.onProgress);
    // Step 7: secondary's "I_APPLICATION_STARTED" acknowledgement.
    const secondaryAckByte = (await transport.read(1, byteTimeoutMs * 20))[0];
    if (secondaryAckByte !== secondaryAck) {
        throw new BootmodeHandshakeError(`secondary loader did not start: expected 0x${secondaryAck
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()}, got 0x${secondaryAckByte.toString(16).padStart(2, '0').toUpperCase()}`, 'secondary-ack');
    }
}
async function uploadEchoed(transport, data, stage, interByteDelayMs, _byteTimeoutMs, onProgress) {
    // Echo verification happens inside transport.write() when the
    // transport's hasAdapterEcho is enabled (default for raw K-line
    // cables). We just need to upload byte-by-byte so the BSL/primary
    // loader can stage each byte at the right RAM offset.
    for (let i = 0; i < data.length; i++) {
        try {
            await transport.write(data.subarray(i, i + 1));
        }
        catch (err) {
            throw new BootmodeHandshakeError(`byte ${i}/${data.length} (TX=0x${data[i]
                .toString(16)
                .padStart(2, '0')
                .toUpperCase()}): ${err.message}`, stage);
        }
        if (onProgress)
            onProgress(stage, i + 1, data.length);
        if (interByteDelayMs > 0)
            await sleep(interByteDelayMs);
    }
}
//# sourceMappingURL=handshake.js.map